import type { ReactElement } from 'react';
import { Container, Grid, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ButtonGroup, PrimaryButton } from '@noah-labs/fe-shared-ui-components';
import type { Maybe } from '../../../generated/types';
import { InquiryButton } from '../../components/buttons/InquiryButton';
import { HeroAnimatedLayers } from './HeroAnimatedLayers';

type PpHeroSection = {
  buttonLabel1?: Maybe<string> | undefined;
  buttonLabel2?: Maybe<string> | undefined;
  buttonLink?: Maybe<string> | undefined;
  subtitle: Maybe<string> | undefined;
  title: Maybe<string> | undefined;
};

const StyledContainer = styled(Container)(({ theme }) => ({
  height: '700px',
  maxWidth: theme.breakpoints.values.lg,
  position: 'relative',
  [theme.breakpoints.down('lg')]: {
    height: '100%',
  },
}));

const StyledSection = styled('section')(({ theme }) => ({
  overflowX: 'hidden',
  paddingBottom: theme.spacing(12),
  [theme.breakpoints.down('md')]: {
    paddingBottom: theme.spacing(6),
  },
}));

const StyledStack = styled(Stack)(({ theme }) => ({
  alignItems: 'flex-start',
  height: '100%',
  justifyContent: 'center',
  paddingTop: theme.spacing(20),
  position: 'relative',
  zIndex: 1,
  [theme.breakpoints.down('lg')]: {
    maxWidth: '400px',
    padding: theme.spacing(12, 3),
  },
}));

export function HeroSection({
  buttonLabel1,
  buttonLabel2,
  buttonLink,
  subtitle,
  title,
}: PpHeroSection): ReactElement {
  return (
    <StyledSection>
      <StyledContainer disableGutters fixed>
        <HeroAnimatedLayers />
        <Grid container height="100%">
          <Grid height="100%" size={{ lg: 5 }}>
            <StyledStack spacing={5}>
              <Typography component="h1" variant="h3">
                {title}
              </Typography>
              <Typography color="text.dark" variant="paragraphBodyL">
                {subtitle}
              </Typography>
              <ButtonGroup direction="row">
                {buttonLabel1 && (
                  <InquiryButton color="primaryDark" variant="contained">
                    {buttonLabel1}
                  </InquiryButton>
                )}
                {buttonLabel2 && buttonLink && (
                  <PrimaryButton color="primaryBrand" href={buttonLink} variant="outlined">
                    {buttonLabel2}
                  </PrimaryButton>
                )}
              </ButtonGroup>
            </StyledStack>
          </Grid>
          <Grid height="100%" size={{ lg: 7 }} />
        </Grid>
      </StyledContainer>
    </StyledSection>
  );
}
